// Meta tag rules for posts, groups and profiles, shared by the React pages
// (through components/common/SEO) and the meta sidecar (meta-service/), so the
// tags a crawler gets from the server match the ones Helmet renders in the browser.
// Keep this file free of React, browser and process.env access.

import { buildSlugId } from './slug';
import { stripHtmlTags, truncate } from './text';

export const DEFAULT_TITLE = '4um - Connect People and Build Communities Online';
export const DEFAULT_DESCRIPTION =
  'Discover the only employee-owned user guided social media platform. Connect, chat, and study with a global community on 4um.';
export const DEFAULT_OG_IMAGE =
  'https://images.ctfassets.net/ewtdlsoyixc1/3K5S3OBYFYRDvOo4IrJYzf/37eb1ac3739ca2e6b80d94edb593be9d/4um_logo.png';
export const DEFAULT_DOMAIN = 'https://www.4um.com';

export interface MetaConfig {
  domain: string;
  userApiBase: string;
  postApiBase: string;
}

export interface MetaInput {
  title?: string;
  description?: string;
  canonical?: string;
  // Emit no canonical/og:url at all (entity routes before their data loads)
  omitCanonical?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, any>;
  noIndex?: boolean;
}

export interface ResolvedMeta {
  title: string;
  description: string;
  canonical?: string;
  robots: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  ogImage?: string;
  twitterCard: string;
  jsonLd?: Record<string, any>;
}

// Final tag values, the same way SEO.tsx has always computed them.
export const resolveMeta = (input: MetaInput, fallbackCanonical?: string): ResolvedMeta => {
  const cleanTitleStr = stripHtmlTags(input.title);
  const cleanDescStr = stripHtmlTags(input.description || DEFAULT_DESCRIPTION);

  const title = cleanTitleStr
    ? cleanTitleStr.toLowerCase().includes('4um')
      ? cleanTitleStr
      : `${cleanTitleStr} | 4um`
    : DEFAULT_TITLE;
  const description = cleanDescStr || DEFAULT_DESCRIPTION;

  return {
    title,
    description,
    canonical: input.omitCanonical ? undefined : input.canonical || fallbackCanonical,
    robots: input.noIndex ? 'noindex, nofollow' : 'index, follow',
    ogTitle: stripHtmlTags(input.ogTitle) || title,
    ogDescription: stripHtmlTags(input.ogDescription) || description,
    ogType: input.ogType || 'website',
    ogImage: input.ogImage === undefined ? DEFAULT_OG_IMAGE : input.ogImage || undefined,
    twitterCard: input.twitterCard || 'summary_large_image',
    jsonLd: input.jsonLd
  };
};

const trimSlash = (url: string) => url.replace(/\/+$/, '');
const userImageUrl = (cfg: MetaConfig, id: string) => `${trimSlash(cfg.userApiBase)}/user/image/${id}`;
const postMediaUrl = (cfg: MetaConfig, id: string) => `${trimSlash(cfg.postApiBase)}/post/media/${id}`;
const groupImageUrl = (cfg: MetaConfig, id: string) => `${trimSlash(cfg.postApiBase)}/group/image/${id}`;
const groupBannerUrl = (cfg: MetaConfig, id: string) => `${trimSlash(cfg.postApiBase)}/group/banner/${id}`;

// Minimal shapes of the API responses; only the fields used for meta tags.
type Str = string | null;
interface MediaRef { id?: Str }
interface UserLike {
  id?: Str;
  publicId?: Str;
  username?: Str;
  bio?: Str;
  summary?: { userName?: Str; designation?: Str; address?: Str } | null;
  userImage?: MediaRef | null;
}
export interface PostLike {
  id?: Str;
  publicId?: Str;
  postType?: Str;
  title?: Str;
  description?: Str;
  question?: Str;
  createdAt?: Str;
  numComments?: number | null;
  user?: UserLike | null;
  mediaDtos?: Array<MediaRef & { videoDuration?: number | null }> | null;
}
export interface GroupLike {
  id?: Str;
  publicId?: Str;
  name?: Str;
  description?: Str;
  groupImageDto?: MediaRef | null;
  groupBannerDto?: MediaRef | null;
}
export type ProfileLike = UserLike;

export const buildPostMeta = (post: PostLike, cfg: MetaConfig): MetaInput => {
  const isPoll = post.postType === 'POLL';
  const titleSource = isPoll
    ? post.question || post.title
    : post.description || post.title;
  const descriptionSource = isPoll
    ? post.description || post.question || post.title
    : post.description || post.title;
  const cleanTitle = stripHtmlTags(titleSource || '');
  const cleanDescription = stripHtmlTags(descriptionSource || '');
  const postTitle = cleanTitle
    ? truncate(cleanTitle, 60)
    : isPoll ? 'Community Poll' : 'Discussion Post';
  const postDescription = cleanDescription
    ? truncate(cleanDescription, 160)
    : 'Join the discussion on 4um.';
  const authorName = post.user?.username || post.user?.summary?.userName || '4um Member';
  const canonical = `${trimSlash(cfg.domain)}/post/${post.publicId || post.id}`;

  // First still image, else the author's avatar, else the default logo
  const image = (post.mediaDtos || []).find((media) => media?.id && !media.videoDuration);
  const ogImage = image?.id
    ? postMediaUrl(cfg, image.id)
    : post.user?.userImage?.id
    ? userImageUrl(cfg, post.user.userImage.id)
    : undefined;

  return {
    title: postTitle,
    description: postDescription,
    canonical,
    ogType: 'article',
    ogImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'DiscussionForumPosting',
      headline: postTitle,
      articleBody: cleanDescription,
      url: canonical,
      author: {
        '@type': 'Person',
        name: authorName
      },
      datePublished: post.createdAt,
      image: ogImage,
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/CommentAction',
        userInteractionCount: post.numComments || 0
      }
    }
  };
};

export const buildGroupMeta = (group: GroupLike, cfg: MetaConfig): MetaInput => {
  const currentGroupName = group.name || 'Community Group';
  const groupDescription = stripHtmlTags(group.description);
  const seoTitle = `${currentGroupName} Community Group - 4um`;
  const groupDescText = groupDescription
    ? `${currentGroupName} community on 4um: ${groupDescription}. Join, post, and connect with members on 4um.`
    : `Join ${currentGroupName} community on 4um. Discover member discussions, shared posts, and updates from ${currentGroupName} on 4um.`;
  const groupCanonical = `${trimSlash(cfg.domain)}/group/${group.publicId || group.id}`;
  const logo = group.groupImageDto?.id ? groupImageUrl(cfg, group.groupImageDto.id) : undefined;
  const banner = group.groupBannerDto?.id ? groupBannerUrl(cfg, group.groupBannerDto.id) : undefined;

  return {
    title: seoTitle,
    description: groupDescText,
    canonical: groupCanonical,
    ogTitle: `${currentGroupName} - 4um Community Group`,
    ogDescription: groupDescText,
    ogImage: banner || logo,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: currentGroupName,
      url: groupCanonical,
      logo,
      image: banner,
      description: groupDescText
    }
  };
};

export const buildProfileMeta = (user: ProfileLike, cfg: MetaConfig): MetaInput => {
  const summary = user.summary || undefined;
  const profileDisplayName = summary?.userName || user.username || '4um Member';
  const profileHandle = user.username;
  const seoTitle = profileHandle && profileHandle !== profileDisplayName
    ? `${profileDisplayName} (@${profileHandle}) - 4um Profile`
    : `${profileDisplayName} - 4um Profile`;
  const about = stripHtmlTags(user.bio) || summary?.designation || summary?.address;
  const profileDescText = about
    ? `${profileDisplayName} on 4um: ${about}. Connect, chat, and view posts on 4um.`
    : `Connect with ${profileDisplayName} on 4um. Explore ${profileDisplayName}'s profile, posts, communities, and activity on 4um social media platform.`;
  const profilePath = buildSlugId(summary?.userName || user.username, user.publicId || user.id || '');
  const profileCanonical = `${trimSlash(cfg.domain)}/profile/${profilePath}`;
  const profileAvatarImage = user.userImage?.id ? userImageUrl(cfg, user.userImage.id) : undefined;

  return {
    title: seoTitle,
    description: profileDescText,
    canonical: profileCanonical,
    ogTitle: `${profileDisplayName} on 4um`,
    ogDescription: profileDescText,
    ogImage: profileAvatarImage,
    ogType: 'profile',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      name: seoTitle,
      url: profileCanonical,
      mainEntity: {
        '@type': 'Person',
        name: profileDisplayName,
        alternateName: profileHandle || undefined,
        identifier: user.id,
        url: profileCanonical,
        image: profileAvatarImage,
        description: profileDescText,
        jobTitle: summary?.designation || undefined,
        address: summary?.address || undefined
      }
    }
  };
};
